import { ClockIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';

interface CalculatorProps {
  onClose: () => void;
  isFocused: boolean;
  onFocus: () => void;
}

interface HistoryItem {
  expression: string;
  result: string;
}

const Calculator: React.FC<CalculatorProps> = ({ onClose, isFocused, onFocus }) => {
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState<string | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const inputDigit = (digit: string) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const performOperation = (nextOperation: string) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(String(inputValue));
    } else if (operation) {
      const prevValue = parseFloat(previousValue);
      let newValue = prevValue;

      switch (operation) {
        case '+':
          newValue = prevValue + inputValue;
          break;
        case '-':
          newValue = prevValue - inputValue;
          break;
        case '×':
          newValue = prevValue * inputValue;
          break;
        case '÷':
          newValue = prevValue / inputValue;
          break;
        case '%':
          newValue = prevValue % inputValue;
          break;
        default:
          break;
      }

      // Ajouter à l'historique
      if (nextOperation === '=') {
        const expression = `${previousValue} ${operation} ${inputValue}`;
        const result = String(newValue);
        setHistory([{ expression, result }, ...history.slice(0, 9)]); // Garder les 10 derniers
      }

      setDisplay(String(newValue));
      setPreviousValue(String(newValue));
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const handlePercentage = () => {
    const value = parseFloat(display);
    setDisplay(String(value / 100));
  };

  const handleNegative = () => {
    const value = parseFloat(display);
    setDisplay(String(value * -1));
  };

  const buttons = [
    ['C', '±', '%', '÷'],
    ['7', '8', '9', '×'],
    ['4', '5', '6', '-'],
    ['1', '2', '3', '+'],
    ['0', '.', '='],
  ];

  const handleButtonClick = (btn: string) => {
    switch (btn) {
      case 'C':
        clear();
        break;
      case '±':
        handleNegative();
        break;
      case '%':
        handlePercentage();
        break;
      case '+':
      case '-':
      case '×':
      case '÷':
        performOperation(btn);
        break;
      case '=':
        performOperation(btn);
        break;
      case '.':
        inputDecimal();
        break;
      default:
        inputDigit(btn);
        break;
    }
  };

  const getButtonStyle = (btn: string) => {
    if (btn === 'C') {
      return 'bg-red-500 hover:bg-red-600 text-white';
    }
    if (['+', '-', '×', '÷', '='].includes(btn)) {
      return 'bg-blue-600 hover:bg-blue-700 text-white';
    }
    if (['±', '%'].includes(btn)) {
      return 'bg-gray-400 hover:bg-gray-500 text-white';
    }
    return 'bg-gray-200 hover:bg-gray-300 text-gray-900';
  };

  const getButtonWidth = (btn: string) => {
    return btn === '0' ? 'col-span-2' : '';
  };

  // Gestion des touches du clavier
  useEffect(() => {
    // Ne gérer les touches que si la calculatrice a le focus
    if (!isFocused) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Empêcher le comportement par défaut pour certaines touches
      if (['Enter', 'Escape', '+', '-', '*', '/', '.'].includes(e.key)) {
        e.preventDefault();
      }

      // Chiffres 0-9
      if (e.key >= '0' && e.key <= '9') {
        handleButtonClick(e.key);
      }
      // Opérateurs
      else if (e.key === '+') {
        handleButtonClick('+');
      } else if (e.key === '-') {
        handleButtonClick('-');
      } else if (e.key === '*') {
        handleButtonClick('×');
      } else if (e.key === '/') {
        handleButtonClick('÷');
      }
      // Point décimal
      else if (e.key === '.' || e.key === ',') {
        handleButtonClick('.');
      }
      // Égal (Enter ou =)
      else if (e.key === 'Enter' || e.key === '=') {
        handleButtonClick('=');
      }
      // Effacer (Escape, Delete, ou c/C)
      else if (e.key === 'Escape' || e.key === 'Delete' || e.key.toLowerCase() === 'c') {
        handleButtonClick('C');
      }
      // Backspace pour effacer le dernier chiffre
      else if (e.key === 'Backspace') {
        if (display.length > 1) {
          setDisplay(display.slice(0, -1));
        } else {
          setDisplay('0');
        }
      }
      // Pourcentage
      else if (e.key === '%') {
        handleButtonClick('%');
      }
    };

    // Ajouter l'écouteur d'événements
    window.addEventListener('keydown', handleKeyDown);

    // Nettoyer l'écouteur lors du démontage
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [display, previousValue, operation, waitingForOperand, isFocused]);

  return (
    <div
      className={`bg-gradient-to-br from-slate-50 to-gray-100 rounded-lg w-80 overflow-hidden transition-all duration-200 ${
        isFocused ? 'ring-4 ring-blue-500 ring-opacity-50' : ''
      }`}
      style={{
        boxShadow:
          '0 20px 60px rgba(0, 0, 0, 0.3), 0 10px 30px rgba(0, 0, 0, 0.22), 0 0 0 1px rgba(0, 0, 0, 0.1)',
      }}
      onClick={onFocus}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-700 px-4 py-3 flex items-center justify-between cursor-move select-none">
        <h3 className="text-white font-semibold text-lg">Calculatrice</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="p-1.5 hover:bg-blue-500 rounded transition-colors"
            title="Historique"
          >
            <ClockIcon className="h-5 w-5 text-white" />
          </button>
          <button onClick={onClose} className="p-1.5 hover:bg-blue-500 rounded transition-colors">
            <svg
              className="h-5 w-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Display */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 px-4 py-6">
        <div className="text-right text-4xl font-light text-white break-all">{display}</div>
      </div>

      {/* History Panel */}
      {showHistory && history.length > 0 && (
        <div className="bg-gray-50 border-b border-gray-200 max-h-40 overflow-y-auto">
          <div className="px-4 py-2">
            <h4 className="text-xs font-semibold text-gray-600 mb-2">Historique</h4>
            {history.map((item, index) => (
              <div
                key={index}
                className="text-sm text-gray-700 py-1 hover:bg-gray-100 px-2 rounded cursor-pointer"
                onClick={() => {
                  setDisplay(item.result);
                  setShowHistory(false);
                }}
              >
                <div className="flex justify-between">
                  <span className="text-gray-500">{item.expression}</span>
                  <span className="font-semibold">{item.result}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Buttons */}
      <div className="p-4 bg-gradient-to-br from-gray-300 to-slate-200">
        <div className="grid grid-cols-4 gap-2">
          {buttons.map((row, rowIndex) => (
            <React.Fragment key={rowIndex}>
              {row.map(btn => (
                <button
                  key={btn}
                  onClick={() => handleButtonClick(btn)}
                  className={`${getButtonStyle(btn)} ${getButtonWidth(btn)} py-4 rounded-md font-semibold text-lg transition-all duration-150 active:scale-95 shadow-sm hover:shadow-md`}
                >
                  {btn}
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Calculator;
